import {Component} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ButtonModule} from 'primeng/button';
import {TableModule} from 'primeng/table';
import {CampaignService} from '../../../services/campaign.service';
import {AgentService} from '../../core/agent/services/agent.service';
import {ConfirmationService, MessageService} from 'primeng/api';
import {ToastModule} from 'primeng/toast';
import {DropdownModule} from 'primeng/dropdown';
import {Router} from '@angular/router';
import {DialogModule} from 'primeng/dialog';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Campaign} from '../../../models/campaign';
import {Agent} from '../../../models/users';
import { AgentCampaign } from '../../../models/campaign';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

// interface CampaignState {
//   name: string;
//   type: string;
//   state: 'Activo' | 'Inactivo' | 'Pausado';
//   priority: number;
//   ratio: string;
//   currentAction: string;
//   stateSince: string;
// }

@Component({
  selector: 'app-campaign-supervisor',
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    TableModule,
    ToastModule,
    DialogModule,
    ConfirmDialogModule,
    DialogModule,
    FormsModule,
    DropdownModule,
    ReactiveFormsModule,
    TranslateModule,
    TooltipModule,
  ],
  providers: [
    MessageService,
    ConfirmationService,
  ],
  templateUrl: './campaign-supervisor.component.html',
  styleUrl: './campaign-supervisor.component.scss'
})
export class CampaignSupervisorComponent {
  campaigns: Campaign[] = [];
  private _loading: boolean = false;
  assignedAgents: Agent[] = [];
  showAgentsDialog: boolean = false;
  agents: any[] = [];
  campaignId: string | undefined;
  isDisabledAsign: boolean = false;
  deletingCampaignId: string | null = null; // Track which campaign is being deleted
  private pendingDeleteCampaign: Campaign | null = null; // Track campaign pending deletion

  agentAssignForm!: FormGroup;

  campaign_selected: Campaign | null = null;

  MIN_DEFAULT_PRIORITY: number = 1;
  MAX_DEFAULT_PRIORITY: number = 3;

  // Getter and setter for loading to manage form control state
  get loading(): boolean {
    return this._loading;
  }

  set loading(value: boolean) {
    this._loading = value;
    this.updateFormControlsLoadingState(value);
  }

  private updateFormControlsLoadingState(isLoading: boolean): void {
    const agentControl = this.agentAssignForm?.get('agent');
    if (agentControl) {
      if (isLoading) {
        agentControl.disable();
      } else {
        agentControl.enable();
      }
    }
  }

  agentPriorities: { [email: string]: number } = {};
  agentPriorityChanged: boolean = false;
  agentPriorityLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private campaignService: CampaignService,
    private agentService: AgentService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private router: Router,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.initForm();
    this.loadCampaigns();
    this.setupAgentSubscription();
  }

  setupAgentSubscription() {
    this.agentAssignForm.get('agent')?.valueChanges.subscribe((selectedAgent) => {
      this.validateSelectedAgent();
    });
  }

  validateSelectedAgent() {
    const selectedAgent = this.agentAssignForm.get('agent')?.value;

    if (!selectedAgent || !this.assignedAgents?.length) {
      this.isDisabledAsign = false;
      return;
    }

    this.isDisabledAsign = this.assignedAgents.some(
      assignedAgent => assignedAgent.email === selectedAgent.email
    );
  }

  initForm() {
    this.agentAssignForm = this.fb.group({
      agent: [{value: '', disabled: false}, Validators.required],
    });
  }

  loadCampaigns() {
     this.loading = true;

    const raw = sessionStorage.getItem('workpsaceId');
    if (!raw) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('supervisor.campaigns.common.error'), detail: this.translate.instant('supervisor.campaigns.toast.workspaceUnavailable') });
      this.loading = false;
      return;
    }

    let workspace: any;
    try {
      workspace = JSON.parse(raw);
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('supervisor.campaigns.common.error'), detail: this.translate.instant('supervisor.campaigns.toast.workspaceInvalid') });
      this.loading = false;
      return;
    }

    const workspaceId = String(workspace?.id || workspace?.workspaceId || '');

    if (!workspaceId) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('supervisor.campaigns.common.error'), detail: this.translate.instant('supervisor.campaigns.toast.workspaceIdMissing') });
      this.loading = false;
      return;
    }

    this.campaignService.getCampaignsByWorkspaceId(workspaceId).subscribe({
      next: (campaigns) => {
        this.campaigns = campaigns;
        this.loading = false;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('supervisor.campaigns.common.error'),
          detail: this.translate.instant('supervisor.campaigns.toast.loadCampaignsError')
        });
        this.loading = false;
      }
    });
  }

  // Add a helper to update priorities mapping
  private updateAgentPrioritiesFromCampaignConfig() {
    if (
      this.campaign_selected &&
      this.campaign_selected.agent_config &&
      this.campaign_selected.agent_config.enable_priority_agent
    ) {
      const orderPriority: AgentCampaign[] = this.campaign_selected.agent_config.order_priority_agent || [];

      this.agentPriorities = {};
      for (const agent of this.assignedAgents) {
        const found = orderPriority.find(a => a.agent_id === agent.id);

        this.agentPriorities[agent.email] = found ? found.priority : this.MIN_DEFAULT_PRIORITY;
      }
      this.agentPriorityChanged = false;
    } else {
      this.agentPriorities = {};
      this.agentPriorityChanged = false;
    }
  }

  showAgentsModal(campaignId: string) {
    this.assignedAgents = [];
    this.agentAssignForm.reset();

    this.campaign_selected = this.campaigns.find(c => c.id === campaignId) || null;
    this.campaignId = campaignId;
    this.showAgentsDialog = true;
    this.loadAgents();
    this.getAssignedAgents();
    // No need to set agentPriorities here, it will be set after agents are loaded
  }

  loadAgents() {
    this.loading = true;

    const raw = sessionStorage.getItem('workpsaceId');
    if (!raw) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('supervisor.campaigns.common.error'), detail: this.translate.instant('supervisor.campaigns.toast.workspaceUnavailable') });
      this.loading = false;
      return;
    }

    let workspace: any;
    try {
      workspace = JSON.parse(raw);
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('supervisor.campaigns.common.error'), detail: this.translate.instant('supervisor.campaigns.toast.workspaceInvalid') });
      this.loading = false;
      return;
    }

    const workspaceId = String(workspace?.id || workspace?.workspaceId || '');

    this.agentService.getAgentsByWorkspace(workspaceId, 'agent').subscribe({
      next: (agents: Agent[]) => {
        this.agents = agents;
        this.loading = false;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('supervisor.campaigns.common.error'),
          detail: this.translate.instant('supervisor.campaigns.toast.loadAgentsError')
        });
        this.loading = false;
      }
    });
  }

  assignAgent() {
    if (this.agentAssignForm.value && this.campaignId) {
      const agentEmail = this.agentAssignForm.value.agent.email;
      const workspaceId = this.resolveCurrentWorkspaceId();

      if (!workspaceId) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('supervisor.campaigns.common.error'),
          detail: this.translate.instant('supervisor.campaigns.toast.workspaceUnavailableAssign')
        });
        return;
      }

      this.agentService.assignCampaignAgentByWorkspace(agentEmail, this.campaignId, workspaceId)
        .subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('supervisor.campaigns.common.success'),
              detail: this.translate.instant('supervisor.campaigns.toast.agentAssigned')
            });
            this.getAssignedAgents(); // This will update priorities after agents are loaded
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('supervisor.campaigns.common.error'),
              detail: this.translate.instant('supervisor.campaigns.toast.assignAgentError')
            });
          }
        });
    }
  }

  getStateColor(state: string): string {
    switch (state) {
      case 'Activo': return '#4CAF50';
      case 'Inactivo': return '#F44336';
      case 'Pausado': return '#FFC107';
      default: return '#9E9E9E';
    }
  }

  deleteassignedAgent(agent_email: string) {
    if(!this.campaignId) return;
    const workspaceId = this.resolveCurrentWorkspaceId();

    if (!workspaceId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('supervisor.campaigns.common.error'),
        detail: this.translate.instant('supervisor.campaigns.toast.workspaceUnavailableUnassign')
      });
      return;
    }

    // Find the agent to get their name for the confirmation message
    const agent = this.assignedAgents.find(a => a.email === agent_email);
    const agentName = agent ? agent.username : agent_email;

    this.confirmationService.confirm({
      key: 'agentRemove', // Unique key for agent removal confirmation
      message: this.translate.instant('supervisor.campaigns.confirm.unassignAgentMessage', { name: agentName }),
      header: this.translate.instant('supervisor.campaigns.removeAgentDialog.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('supervisor.campaigns.removeAgentDialog.accept'),
      rejectLabel: this.translate.instant('supervisor.campaigns.common.cancel'),
      accept: () => {
        this.loading = true;
        this.agentService.removeCampaignFromAgentByWorkspace(agent_email, this.campaignId!, workspaceId)
          .subscribe({
            next: () => {
              this.assignedAgents = this.assignedAgents.filter(agent => agent.email !== agent_email);

              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('supervisor.campaigns.common.success'),
                detail: this.translate.instant('supervisor.campaigns.toast.agentUnassigned', { name: agentName })
              });

              this.getAssignedAgents(); // This will update priorities after agents are loaded
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('supervisor.campaigns.common.error'),
                detail: this.translate.instant('supervisor.campaigns.toast.unassignAgentError')
              });
            },
            complete: () => {
              this.loading = false;
            }
          });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('supervisor.campaigns.common.cancelled'),
          detail: this.translate.instant('supervisor.campaigns.toast.unassignCancelled'),
          life: 3000
        });
      }
    });
  }

  private resolveCurrentWorkspaceId(): string {
    const raw = sessionStorage.getItem('workpsaceId')
      || sessionStorage.getItem('workspace')
      || sessionStorage.getItem('workspaceId')
      || localStorage.getItem('workpsaceId')
      || localStorage.getItem('workspace')
      || localStorage.getItem('workspaceId');

    if (!raw) {
      return '';
    }

    try {
      const workspace = JSON.parse(raw);
      return String(workspace?.id || workspace?.workspaceId || '').trim();
    } catch {
      return '';
    }
  }

  getAssignedAgents() {
    if(!this.campaignId) return
    this.agentService.getAgentsByCampaign(this.campaignId)
    .subscribe({
      next: (agents: Agent[]) => {
        this.assignedAgents = agents;
        this.validateSelectedAgent();
        this.updateAgentPrioritiesFromCampaignConfig(); // Always update priorities after agents are loaded
      },
      error: (error) => {
        console.error('Error obteniendo agentes:', error);
      }
    });
  }

  // Called from template to safely extract number from event
  onAgentPriorityInput(email: string, event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input && input.value !== undefined ? Number(input.value) : this.MIN_DEFAULT_PRIORITY;

    // console.log(`Input value for ${email}:`, value);
    // Ensure value is a valid number and at least MIN_DEFAULT_PRIORITY
    if (isNaN(value)) return;

    if (value < this.MIN_DEFAULT_PRIORITY) {
      input.value = this.MIN_DEFAULT_PRIORITY.toString();
    }
    if (value > this.MAX_DEFAULT_PRIORITY) {
      input.value = this.MAX_DEFAULT_PRIORITY.toString();
    }

    this.onAgentPriorityChange(email, value);
  }

  // Called when a priority input changes
  onAgentPriorityChange(email: string, value: number) {

    if (!this.campaignId || !this.campaign_selected) return;


    this.agentPriorities[email] = Math.max(0, value || this.MIN_DEFAULT_PRIORITY); // Ensure priority is at least MIN_DEFAULT_PRIORITY
    this.agentPriorityChanged = true;
  }

  // Save priorities to backend
  saveAgentPriorities() {
    if (!this.campaignId || !this.campaign_selected) return;
    this.agentPriorityLoading = true;
    // Build AgentCampaign[] from assignedAgents and agentPriorities
    const agentCampaigns: AgentCampaign[] = this.assignedAgents.map(agent => ({
      agent_id: agent.id,
      priority: this.agentPriorities[agent.email] ?? 0
    }));
    this.campaignService.updateCampaignAgentPriority(this.campaignId, agentCampaigns)
      .subscribe({
        next: (campaign:Campaign) => {
          const updated = campaign.agent_config?.order_priority_agent || [];
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('supervisor.campaigns.toast.prioritiesUpdatedSummary'),
            detail: this.translate.instant('supervisor.campaigns.toast.prioritiesUpdatedDetail')
          });
          // Update local campaign_selected config
          if (this.campaign_selected && this.campaign_selected.agent_config) {
            this.campaign_selected.agent_config.order_priority_agent = updated;
          }
          this.agentPriorityChanged = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('supervisor.campaigns.common.error'),
            detail: this.translate.instant('supervisor.campaigns.toast.prioritiesSaveError')
          });
        },
        complete: () => {
          this.agentPriorityLoading = false;
        }
      });
  }



  navigateTo(id: string, isEdit: boolean = false, isNew: boolean = false) {
    if (isNew) {
      this.router.navigate(['campaign', ''])
    }
    if (isEdit) {
      this.router.navigate(['campaign', id], { queryParams: { edit: true }})
    } else {
      this.router.navigate(['campaign', id])
    }
  }

  confirmDeleteCampaign(campaign: Campaign) {
    this.pendingDeleteCampaign = campaign;

    this.confirmationService.confirm({
      key: 'campaignDelete', // This must match the key in the template
      message: this.translate.instant('supervisor.campaigns.confirm.deleteCampaignMessage', { name: campaign.name }),
      header: this.translate.instant('supervisor.campaigns.deleteDialog.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('supervisor.campaigns.deleteDialog.accept'),
      rejectLabel: this.translate.instant('supervisor.campaigns.common.cancel'),
      accept: () => {
        this.acceptConfirmation();
      },
      reject: () => {
        this.rejectConfirmation();
      }
    });
  }

  acceptConfirmation() {
    if (this.pendingDeleteCampaign && this.pendingDeleteCampaign.id) {
      this.deleteCampaign(this.pendingDeleteCampaign.id);
    }
  }

  rejectConfirmation() {
    this.pendingDeleteCampaign = null;
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('supervisor.campaigns.common.cancelled'),
      detail: this.translate.instant('supervisor.campaigns.toast.deleteCancelled'),
      life: 3000
    });
  }

  deleteCampaign(campaignId: string) {
    this.deletingCampaignId = campaignId;
    this.loading = true;

    this.campaignService.removeCampaignById(campaignId).subscribe({
      next: () => {
        // Remove campaign from local array for immediate UI update
        this.campaigns = this.campaigns.filter(campaign => campaign.id !== campaignId);

        // Update campaigns storage
        this.campaignService.setCampaignsStorage(this.campaigns);

        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('supervisor.campaigns.common.success'),
          detail: this.translate.instant('supervisor.campaigns.toast.campaignDeleted', { name: this.pendingDeleteCampaign?.name || '' }),
          life: 5000
        });

        // Reset pending delete campaign
        this.pendingDeleteCampaign = null;

        // Reload campaigns to ensure data consistency
        this.loadCampaigns();
      },
      error: (error) => {
        console.error('Error deleting campaign:', error);

        let errorMessage = this.translate.instant('supervisor.campaigns.toast.deleteError');

        // Handle specific error cases
        if (error.status === 404) {
          errorMessage = this.translate.instant('supervisor.campaigns.toast.deleteErrorNotFound');
        } else if (error.status === 403) {
          errorMessage = this.translate.instant('supervisor.campaigns.toast.deleteErrorForbidden');
        } else if (error.status === 409) {
          errorMessage = this.translate.instant('supervisor.campaigns.toast.deleteErrorConflict');
        } else if (error.status === 500) {
          errorMessage = this.translate.instant('supervisor.campaigns.toast.deleteErrorServer');
        }

        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('supervisor.campaigns.toast.deleteErrorSummary'),
          detail: errorMessage,
          life: 7000
        });

        // Reset pending delete campaign on error
        this.pendingDeleteCampaign = null;
      },
      complete: () => {
        this.loading = false;
        this.deletingCampaignId = null;
      }
    });
  }

  // Enhanced method to check if a specific campaign is being deleted
  isCampaignBeingDeleted(campaignId: string): boolean {
    return this.deletingCampaignId === campaignId;
  }
}
